import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';

/**
 * Knowledge Base details panel component for viewing and editing Knowledge Base files
 */
const KnowledgeBaseDetailsPanel = ({
  knowledgeBase,
  onUpdate,
  onViewAssociations,
  onEditContent,
  onDelete,
  onViewMetrics,
  onClose
}) => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  const { theme } = useTheme();

  // State management
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    description: ''
  });
  const [kbDetails, setKbDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(true);

  /**
   * Fetch detailed Knowledge Base information
   */
  const fetchKBDetails = async () => {
    try {
      setLoadingDetails(true);
      const token = await getToken();

      const response = await apiRequest(`/api/admin/knowledge-base/${knowledgeBase.id}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      setKbDetails(data);
      setFormData({
        name: data.name || '',
        content: data.content || '',
        description: data.description || ''
      });

    } catch (error) {
      console.error('Error fetching Knowledge Base details:', error);
      showToast('Failed to load Knowledge Base details', 'error');
    } finally {
      setLoadingDetails(false);
    }
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const token = await getToken();

      const response = await apiRequest(`/api/admin/knowledge-base/${knowledgeBase.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const updatedKB = await response.json();
      onUpdate(updatedKB);
      setEditing(false);
      showToast('Knowledge Base file updated successfully', 'success');

      // Refresh details
      await fetchKBDetails();

    } catch (error) {
      console.error('Error updating Knowledge Base:', error);
      showToast(error.message || 'Failed to update Knowledge Base file', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle Knowledge Base deletion
   */
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this Knowledge Base file? This action cannot be undone and will notify the owner.')) {
      return;
    }

    try {
      setLoading(true);
      const token = await getToken();

      const response = await apiRequest(`/api/admin/knowledge-base/${knowledgeBase.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      showToast('Knowledge Base file deleted successfully', 'success');
      onClose();

    } catch (error) {
      console.error('Error deleting Knowledge Base:', error);
      showToast(error.message || 'Failed to delete Knowledge Base file', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Format file size for display
   */
  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Load Knowledge Base details when component mounts or knowledgeBase changes
  useEffect(() => {
    if (knowledgeBase?.id) {
      fetchKBDetails();
    }
  }, [knowledgeBase?.id]);

  if (loadingDetails) {
    return (
      <div className={`rounded-lg border p-6 ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400"></div>
          <span className={`ml-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Loading Knowledge Base details...</span>
        </div>
      </div>
    );
  }

  if (!kbDetails) {
    return (
      <div className={`rounded-lg border p-6 ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="text-center py-8">
          <div className="text-4xl mb-4">⚠️</div>
          <h3 className={`text-lg font-medium mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Failed to load Knowledge Base details</h3>
          <p className={`mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Unable to fetch detailed information for this Knowledge Base file.</p>
          <button
            onClick={fetchKBDetails}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors dark:text-white"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border overflow-hidden ${
      theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    }`}>
      {/* Header */}
      <div className={`px-6 py-4 border-b flex justify-between items-center ${
        theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
      }`}>
        <div>
          <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Knowledge Base File Details</h3>
          <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            ID: {knowledgeBase.id}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onViewAssociations(knowledgeBase)}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors dark:text-white"
          >
            Associations
          </button>
          <button
            onClick={() => onEditContent(knowledgeBase)}
            className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors dark:text-white"
          >
            Edit Content
          </button>
          <button
            onClick={() => onViewMetrics(knowledgeBase)}
            className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors dark:text-white"
          >
            Metrics
          </button>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="px-3 py-1 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors dark:text-white"
            >
              Edit Info
            </button>
          )}
          <button
            onClick={onClose}
            className={`transition-colors ${
              theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            ✕
          </button>
        </div>
      </div>

      <div className="p-6">
        {editing ? (
          /* Edit Form */
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                placeholder="Optional description..."
              />
            </div>

            {/* Content */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Content
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => handleInputChange('content', e.target.value)}
                rows={12}
                className={`w-full px-3 py-2 border rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                placeholder="Knowledge Base file content..."
              />
              <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Content length: {formData.content.length} characters
              </p>
            </div>

            {/* Form Actions */}
            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => onDelete(knowledgeBase)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors dark:text-white"
                disabled={loading}
              >
                Delete Knowledge Base File
              </button>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    setFormData({
                      name: kbDetails.name || '',
                      content: kbDetails.content || '',
                      description: kbDetails.description || ''
                    });
                  }}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    theme === 'dark'
                      ? 'bg-gray-600 text-white hover:bg-gray-700'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors dark:text-white"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>
        ) : (
          /* View Mode */
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className={`text-sm font-medium mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Basic Information</h4>
                <div className="space-y-3">
                  <div>
                    <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Name</span>
                    <p className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{kbDetails.name || 'Unnamed'}</p>
                  </div>
                  <div>
                    <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Owner</span>
                    <p className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                      {kbDetails.user_name || kbDetails.user_email || 'Unknown User'}
                    </p>
                    {kbDetails.user_email && (
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{kbDetails.user_email}</p>
                    )}
                  </div>
                  <div>
                    <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Status</span>
                    <p className={`capitalize ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{kbDetails.status || 'Active'}</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className={`text-sm font-medium mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>File Information</h4>
                <div className="space-y-3">
                  <div>
                    <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>File Size</span>
                    <p className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{formatFileSize(kbDetails.file_size)}</p>
                  </div>
                  <div>
                    <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Chunks</span>
                    <p className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{kbDetails.chunk_count || 'N/A'}</p>
                  </div>
                  <div>
                    <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Created</span>
                    <p className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{formatDate(kbDetails.created_at)}</p>
                  </div>
                  <div>
                    <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Last Updated</span>
                    <p className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{formatDate(kbDetails.updated_at)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            {kbDetails.description && (
              <div>
                <h4 className={`text-sm font-medium mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Description</h4>
                <p className={`p-3 rounded-md ${
                  theme === 'dark' 
                    ? 'text-gray-300 bg-gray-700' 
                    : 'text-gray-700 bg-gray-50'
                }`}>
                  {kbDetails.description}
                </p>
              </div>
            )}

            {/* Associated Parrots */}
            {kbDetails.associated_parrots && kbDetails.associated_parrots.length > 0 && (
              <div>
                <h4 className={`text-sm font-medium mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Associated Parrots ({kbDetails.associated_parrots.length})
                </h4>
                <div className="space-y-2">
                  {kbDetails.associated_parrots.map((parrot) => (
                    <div key={parrot.id} className={`flex items-center justify-between p-3 rounded-md ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                    }`}>
                      <div>
                        <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{parrot.name}</p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Owner: {parrot.user_name || parrot.user_email}</p>
                      </div>
                      <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>ID: {parrot.id}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Performance Metrics */}
            {kbDetails.metrics && (
              <div>
                <h4 className={`text-sm font-medium mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Performance Metrics</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className={`p-3 rounded-md text-center ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <p className="text-2xl font-bold text-teal-400">{kbDetails.metrics.queries || 0}</p>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Total Queries</p>
                  </div>
                  <div className={`p-3 rounded-md text-center ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <p className="text-2xl font-bold text-blue-400">{kbDetails.metrics.avg_relevance || 'N/A'}</p>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Avg Relevance</p>
                  </div>
                  <div className={`p-3 rounded-md text-center ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <p className="text-2xl font-bold text-green-400">{kbDetails.metrics.last_used || 'Never'}</p>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Last Used</p>
                  </div>
                  <div className={`p-3 rounded-md text-center ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <p className="text-2xl font-bold text-purple-400">{kbDetails.metrics.hit_rate || 'N/A'}</p>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Hit Rate</p>
                  </div>
                </div>
              </div>
            )}

            {/* Content Preview */}
            <div>
              <h4 className={`text-sm font-medium mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Content Preview</h4>
              <div className={`p-4 rounded-md max-h-64 overflow-y-auto ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <pre className={`text-sm whitespace-pre-wrap font-mono ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {kbDetails.content ?
                    (kbDetails.content.length > 1000 ?
                      kbDetails.content.substring(0, 1000) + '\n\n... (truncated)' :
                      kbDetails.content
                    ) :
                    'No content available'
                  }
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBaseDetailsPanel;
